import { ref, readonly } from 'vue';

/**
 * Composable for handling async operations with loading, error, and success states.
 *
 * @param asyncFn - The async function to execute
 * @returns Object containing reactive state and run function
 */
export function useAsyncRequest<TArgs extends unknown[], TReturn>(asyncFn: (...args: TArgs) => Promise<TReturn>) {
  let promise: Promise<TReturn> | null = null;
  /** The async request is running */
  const isLoading = ref(false);
  /** The async request error once finished and failed */
  const error = ref<unknown | null>(null);
  /** The async request finished and succeeded */
  const success = ref(false);

  function clear() {
    isLoading.value = false;
    error.value = null;
    success.value = false;
  }

  /** Trigger the actual async request and return its Promise */
  async function run(...args: TArgs): Promise<TReturn | undefined> {
    if (isLoading.value && promise) return promise;

    clear();

    isLoading.value = true;

    try {
      promise = asyncFn(...args);
      const result = await promise;
      success.value = true;
      return result;
    } catch (err: unknown) {
      error.value = err;
    } finally {
      isLoading.value = false;
    }
  };

  return {
    isLoading: readonly(isLoading),
    error: readonly(error),
    success: readonly(success),
    run,
    refresh: (...args: TArgs) => run(...args),
    clear,
  };
}